/**
 * Editor-side line-edge helpers. The resolver itself (LineEdgeRef,
 * resolveLineRef, lineRefSegments, lineGaps) is model domain and lives in the
 * @kaiizen/kai-floor-plan package — re-exported here so editor consumers keep
 * one import path. What stays here is Selection-aware: how a segment is
 * ADDRESSED by a selection, and the one predicate that answers "is this
 * segment selected".
 *
 * A segment on a primary edge has exactly one canonical selection: an alcove
 * (which carries walls and corners of its own) is `{ kind: 'alcove' }`;
 * doors, windows, and openings are `{ kind: 'lineSeg', ref: { k: 'pe' } }`.
 * `normalizeSelection` (selection.ts) enforces that rule inside the selection
 * funnel, `select`, so no code path can leave a door half-selected as an
 * alcove: dims, handles, trash icon, and inspector all read the same selection.
 */

import {
  type EdgeName,
  edgeSegments,
  type FloorPlanEdgeSegment,
  type FloorPlanRoom,
  type LineEdgeRef,
  lineRefSegments,
  sameLineRef,
} from '@kaiizen/kai-floor-plan';
import type { Selection } from './editorState';

export { lineGaps, lineRefSegments, type ResolvedLine, resolveLineRef } from '@kaiizen/kai-floor-plan';

/** The segment the current lineSeg selection points at, if any. */
export function getLineSegment(st: { shape: FloorPlanRoom; selection: Selection }): FloorPlanEdgeSegment | null {
  const sel = st.selection;
  if (sel.kind !== 'lineSeg') return null;
  return lineRefSegments(st.shape, sel.ref)[sel.si] ?? null;
}

/** The host line + index a segment-addressing selection points at (alcove / alcoveCorner / lineSeg); null for everything else. */
export function selectionSegmentRef(selection: Selection): { ref: LineEdgeRef; si: number } | null {
  if (selection.kind === 'lineSeg') return { ref: selection.ref, si: selection.si };
  if (selection.kind === 'alcove' || selection.kind === 'alcoveCorner') {
    return { ref: { k: 'pe', edge: selection.edge }, si: selection.index };
  }
  return null;
}

/** How the editor addresses a segment on a primary edge: alcoves by kind, the rest as line segments. */
export function segmentSelectionFor(edge: EdgeName, index: number, segment: FloorPlanEdgeSegment): Selection {
  return segment.edgeSegmentType === 'alcove'
    ? { kind: 'alcove', edge, index }
    : { kind: 'lineSeg', ref: { k: 'pe', edge }, si: index };
}

/** The canonical selection of the segment at `index` on `edge` (the edge itself when there is none). */
export function primaryEdgeSegmentSelection(shape: FloorPlanRoom, edge: EdgeName, index: number): Selection {
  const segment = edgeSegments(shape, edge)[index];
  return segment ? segmentSelectionFor(edge, index, segment) : { kind: 'edge', edge };
}

/**
 * THE predicate every segment renderer asks: is the segment at (`ref`, `si`)
 * the selected one? Reads the selection through `selectionSegmentRef`, so an
 * alcove selected by kind and a door selected as a line segment both answer
 * for their own segment, and no renderer hand-rolls a kind check.
 */
export function isLineSegmentSelected(selection: Selection, ref: LineEdgeRef, si: number): boolean {
  const target = selectionSegmentRef(selection);
  return !!target && target.si === si && sameLineRef(target.ref, ref);
}
